import { supabase } from '@/lib/supabase';

export const DISPATCH_API = process.env.REACT_APP_DISPATCH_API_URL || 'https://dispatch-api.brxs.ca';

export async function dispatchHeaders(extra = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    ...extra,
  };
}

export async function dispatchFetch(path, options = {}) {
  const { headers: extraHeaders, ...rest } = options;
  const headers = await dispatchHeaders(extraHeaders || {});
  return fetch(`${DISPATCH_API}${path}`, { ...rest, headers });
}

// Design tokens — matches Bricks-Dispatch constants/Theme.js
// Surfaces and text follow the app/system colour scheme through index.css vars:
// night/moon in dark mode (same values as before), paper/ink in light mode.
// Brand and status hex stay literal because callers append alpha (T.bad + '33').
export const T = {
  night: 'var(--c-bg)',
  nightSoft: 'var(--c-bg-soft)',
  nightDeep: 'var(--c-bg-deep)',
  nightHair: 'var(--c-hair)',
  nightHairS: 'var(--c-hair-s)',
  moon: 'var(--c-ink)',
  moonSoft: 'var(--c-ink-soft)',
  // Text tokens stay at least 4.5:1 on their surfaces in both themes (WCAG AA).
  moonMute: 'var(--c-ink-mute)',
  moonFaint: 'var(--c-ink-faint)',
  // Theme-following status text (readable on night and on paper).
  okText: 'var(--c-ok)',
  badText: 'var(--c-bad)',
  attnText: 'var(--c-attn)',
  warnText: 'var(--c-warn)',
  graphite: 'var(--d-graphite)',
  graphitePress: 'var(--d-graphite-press)',
  moonPress: 'var(--d-press)',
  brick: '#D2542B',
  brickDeep: '#A8401F',
  brickSoft: '#F4D9CC',
  brickTint: '#FBEFE8',
  ok: '#4A6B3C',
  warn: '#B8741F',
  bad: '#8C2F24',
  radius: '2px',
};

// Web font equivalents
export const F = {
  heading: "'General Sans', sans-serif",
  mono: "'Ioskeley Mono', monospace",
};

// Shorthand for solid border (React Native auto-applies solid; web needs explicit borderStyle)
export function bd(color, width = 1) {
  return { borderWidth: width, borderStyle: 'solid', borderColor: color };
}

// Format HH:MM time string or ISO datetime → human-readable 12h
export function fmtTime(t) {
  if (!t) return '—';
  if (/^\d{2}:\d{2}$/.test(t)) {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
  }
  const d = new Date(t);
  return isNaN(d.getTime()) ? t : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  if (d.toDateString() === today) return 'Today';
  if (d.toDateString() === yesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtMoney(amount) {
  if (amount == null) return '—';
  return `$${Number(amount).toLocaleString('en-US')}`;
}

export function initials(name) {
  if (!name) return '??';
  return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}
